import {useCallback, useEffect, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {useSearchParams} from "react-router-dom";
import type {ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {useCurrentAppId} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {ReservationActions} from "../../../../store/reservations.reducer";
import {
    defaultStatusFiltersBasedOnView,
    statusFilterOptionsBasedOnView,
} from "../../../../utils/reservation.util";
import {
    FLOORPLAN_QUERY_PARAMS,
    floorplanStorageKeys,
    parseFloorplanStatuses,
    serializeFloorplanStatuses,
} from "../floorplanState";

const FLOORPLAN_STATUSES = statusFilterOptionsBasedOnView("floorplan");

export function getStoredFloorplanStatuses(
    sectorId: string,
    restaurantId?: string,
    queryValue: string | null = null,
): ReservationStatus[] {
    const queryStatuses = parseFloorplanStatuses(queryValue, FLOORPLAN_STATUSES);
    if (queryStatuses) return queryStatuses;

    const restaurantStatuses = restaurantId
        ? parseFloorplanStatuses(
            localStorage.getItem(floorplanStorageKeys(restaurantId).statuses),
            FLOORPLAN_STATUSES,
        )
        : undefined;
    if (restaurantStatuses) return restaurantStatuses;

    const legacyStatuses = parseFloorplanStatuses(
        localStorage.getItem(`selectedStatuses-${sectorId}-floorplan`),
        FLOORPLAN_STATUSES,
    );
    return legacyStatuses ?? defaultStatusFiltersBasedOnView("floorplan");
}

export interface FloorplanStatusSelection {
    statuses: ReservationStatus[];
    setStatuses: (statuses: ReservationStatus[]) => void;
    initialized: boolean;
}

export function useFloorplanStatusSelection(
    sectorId: string,
    restaurantId?: string,
): FloorplanStatusSelection {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const appId = useCurrentAppId();
    const statusesParam = searchParams.get(FLOORPLAN_QUERY_PARAMS.statuses);
    const storedStatuses = useMemo(
        () => getStoredFloorplanStatuses(sectorId, restaurantId, statusesParam),
        [restaurantId, sectorId, statusesParam],
    );
    const [statuses, setStatusState] = useState<ReservationStatus[]>(storedStatuses);
    const [initializedKey, setInitializedKey] = useState<string>();
    const stateKey = restaurantId ? `${sectorId}:${restaurantId}` : undefined;

    useEffect(() => {
        if (!restaurantId || !stateKey) return;

        const serializedStatuses = serializeFloorplanStatuses(storedStatuses);
        setStatusState(storedStatuses);
        localStorage.setItem(floorplanStorageKeys(restaurantId).statuses, serializedStatuses);
        if (statusesParam !== serializedStatuses) {
            setSearchParams(currentSearchParams => {
                const nextSearchParams = new URLSearchParams(currentSearchParams);
                nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.statuses, serializedStatuses);
                return nextSearchParams;
            }, {replace: true});
        }
        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload: {
                activeTab: "floorplan",
                selectedStatuses: storedStatuses,
            },
        });
        setInitializedKey(stateKey);
    }, [
        appId,
        dispatch,
        restaurantId,
        setSearchParams,
        stateKey,
        statusesParam,
        storedStatuses,
    ]);

    const setStatuses = useCallback((nextStatuses: ReservationStatus[]) => {
        if (!restaurantId) return;

        const serializedStatuses = serializeFloorplanStatuses(nextStatuses);
        setStatusState(nextStatuses);
        localStorage.setItem(floorplanStorageKeys(restaurantId).statuses, serializedStatuses);
        setSearchParams(currentSearchParams => {
            const nextSearchParams = new URLSearchParams(currentSearchParams);
            nextSearchParams.set(FLOORPLAN_QUERY_PARAMS.statuses, serializedStatuses);
            return nextSearchParams;
        }, {replace: true});
        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload: {selectedStatuses: nextStatuses},
        });
    }, [appId, dispatch, restaurantId, setSearchParams]);

    return {
        statuses,
        setStatuses,
        initialized: Boolean(stateKey) && initializedKey === stateKey,
    };
}
